import React, { useContext, useEffect, useState } from "react";
import { PostData, PutData } from "../../../../services/FetchData";
import ColorPickerField from "../../../../components/Search/ColorPickerField";
import { Button, Col, Row, Spinner } from "reactstrap";
import { ProductContext } from "../../../../store";
import { toast } from "react-toastify";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";
import JavaScriptCodeEditor from "../../../../components/Search/JavaScriptCodeEditor";

const PLATFORMS = { MAGENTO: "magento", SHOPIFY: "shopify", BIGCOMMERCE: "bigcommerce", WOOCOMMERCE: "woocommerce", DRUPAL: "drupal" };
const PLATFORM_PREFIX = { [PLATFORMS.MAGENTO]: "magento", [PLATFORMS.SHOPIFY]: "shopify", [PLATFORMS.BIGCOMMERCE]: "bigcommerce", [PLATFORMS.DRUPAL]: "drupal", [PLATFORMS.WOOCOMMERCE]: "woocommerce" };

const getDesignApiConfig = (platform) => {
	const base = platform === PLATFORMS.MAGENTO ? "ms2" : "ms3";
	const prefix = PLATFORM_PREFIX[platform];
	return { base, prefix };
};

const AutoComplete = () => {
	const { currentWebsiteId, websiteData, websiteLoader } = useContext(ProductContext);
	const [autoCompleteCustomCss, setAutoCompleteCustomCss] = useState("");
	const [designData, setDesignData] = useState(null);
	const [autoCompleteDesignSaveLoader, setAutoCompleteDesignSaveLoader] = useState(false);
	const [autoCompleteCssSaveLoader, setAutoCompleteCssSaveLoader] = useState(false);
	const [designLoader, setDesignLoader] = useState(true);

	const platform = websiteData?.[currentWebsiteId]?.websiteType;
	const { base, prefix } = getDesignApiConfig(platform || "");
	const isMagento = base === "ms2";

	useEffect(() => {
		if (designData == null) return;
		if (isMagento) {
			setAutoCompleteCustomCss(designData.customCss ?? "");
		} else if (platform === PLATFORMS.WOOCOMMERCE) {
			setAutoCompleteCustomCss(designData.autoCompleteDesign?.customCss ?? "");
		} else {
			setAutoCompleteCustomCss(designData.customCss?.autocomplete ?? "");
		}
	}, [designData, platform, isMagento]);

	useEffect(() => {
		if (!currentWebsiteId || !platform) return;
		getDesignConfig();
	}, [currentWebsiteId, platform]);

	const handleAutoCompleteColorSelection = (value, keyToUpdate) => {
		const updatedObject = {
			...designData,
			autoCompleteDesign: {
				...(designData?.autoCompleteDesign || designData),
				[keyToUpdate]: value,
			},
		};
		setDesignData(updatedObject);
	};

	const getDesignConfig = async () => {
		setDesignLoader(true);
		if (isMagento) {
			PostData(base, `api/v1/${prefix}/getDesignDetails`, { websiteId: currentWebsiteId, type: "autoComplete" }, false, true).then((result) => {
				if (result !== "Invalid" && result !== "undefined" && result?.status === "success") {
					const ac = result.data?.autoCompleteDesign || result.data;
					setDesignData({
						autoCompleteDesign: ac,
						customCss: { autocomplete: result.data?.customCss ?? "" },
					});
					setAutoCompleteCustomCss(result.data?.customCss ?? "");
				}
				setDesignLoader(false);
			});
		} else {
			PostData(base, `api/v1/${prefix}/getDesign`, { websiteId: currentWebsiteId }).then((result) => {
				if (result !== "Invalid" && result !== "undefined" && result?.status === "success") {
					setDesignData(result.data);
				}
				setDesignLoader(false);
			});
		}
	};

	const saveAutoCompleteDesign = async () => {
		setAutoCompleteDesignSaveLoader(true);
		if (isMagento) {
			const ac = designData?.autoCompleteDesign || designData;
			const dataToSend = {
				websiteId: currentWebsiteId,
				background: ac?.background,
				hoverBackground: ac?.hoverBackground,
				label: ac?.label,
				suggestionText: ac?.suggestionText,
				itemTitle: ac?.itemTitle,
				itemDescription: ac?.itemDescription,
				price: ac?.price,
				listPrice: ac?.listPrice,
				sku: ac?.sku,
				viewAllBackground: ac?.viewAllBackground,
				viewAllText: ac?.viewAllText,
			};
			PutData(base, `api/v1/${prefix}/updateDesignAutoComplete`, dataToSend, false, true).then((result) => {
				if (result !== "Invalid" && result !== "undefined") {
					if (result?.status === "success") {
						toast.success(result.message);
						if (result.data) setDesignData((prev) => ({ ...prev, autoCompleteDesign: result.data }));
					} else {
						toast.error(result?.message || result?.message?.message || "Failed to save");
					}
				}
				setAutoCompleteDesignSaveLoader(false);
			});
		} else {
			const data = { ...(designData?.autoCompleteDesign || designData), websiteId: currentWebsiteId };
			PostData(base, `api/v1/${prefix}/updateDesignAutoComplete`, data).then((result) => {
				if (result !== "Invalid" && result !== "undefined") {
					if (result?.status === "success") toast.success(result.message);
					else if (result?.status === "failure") toast.error(result.message);
					else toast.error(result?.message?.message || result?.message);
				}
				setAutoCompleteDesignSaveLoader(false);
			});
		}
	};

	const saveAutoCompleteCss = async () => {
		setAutoCompleteCssSaveLoader(true);
		if (isMagento) {
			PutData(base, `api/v1/${prefix}/updateDesignCustomCss`, {
				websiteId: currentWebsiteId,
				type: "autoComplete",
				customCss: autoCompleteCustomCss,
			}, false, true).then((result) => {
				if (result !== "Invalid" && result !== "undefined" && result?.status === "success") {
					toast.success(result.message);
				} else {
					toast.error(result?.message || "Failed to save");
				}
				setAutoCompleteCssSaveLoader(false);
			});
		} else {
			PostData(base, `api/v1/${prefix}/updateAutoCompleteCss`, {
				autocomplete: autoCompleteCustomCss,
				websiteId: currentWebsiteId,
			}).then((result) => {
				if (result !== "Invalid" && result !== "undefined") {
					if (result?.status === "success") toast.success(result.message);
					else if (result?.status === "failure") toast.error(result.message);
					else toast.error(result?.message?.message || result?.message);
				}
				setAutoCompleteCssSaveLoader(false);
			});
		}
	};

	const acDesign = designData?.autoCompleteDesign || designData;

	const renderColorsComponent = () => {
		if (!acDesign) return null;
		return (
			<div>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Main background</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acBackground" initialColor={acDesign.background} onColorChange={(c) => handleAutoCompleteColorSelection(c, "background")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Label color</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acLabel" initialColor={acDesign.label} onColorChange={(c) => handleAutoCompleteColorSelection(c, "label")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Suggestion text</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acSuggestionText" initialColor={acDesign.suggestionText} onColorChange={(c) => handleAutoCompleteColorSelection(c, "suggestionText")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Item title</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acItemTitle" initialColor={acDesign.itemTitle} onColorChange={(c) => handleAutoCompleteColorSelection(c, "itemTitle")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Item description</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acItemDescription" initialColor={acDesign.itemDescription} onColorChange={(c) => handleAutoCompleteColorSelection(c, "itemDescription")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Price color</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acPrice" initialColor={acDesign.price} onColorChange={(c) => handleAutoCompleteColorSelection(c, "price")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>List price color</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acListPrice" initialColor={acDesign.listPrice} onColorChange={(c) => handleAutoCompleteColorSelection(c, "listPrice")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>SKU color</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acSku" initialColor={acDesign.sku} onColorChange={(c) => handleAutoCompleteColorSelection(c, "sku")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>"View all" background</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acViewAllBackground" initialColor={acDesign.viewAllBackground} onColorChange={(c) => handleAutoCompleteColorSelection(c, "viewAllBackground")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>"View all" text</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acViewAllText" initialColor={acDesign.viewAllText} onColorChange={(c) => handleAutoCompleteColorSelection(c, "viewAllText")} /></Col>
				</Row>
				<Row className="mb-5 align-items-start">
					<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Hover background</h4></label></Col>
					<Col xs={12} md={6}><ColorPickerField key="acHoverBackground" initialColor={acDesign.hoverBackground} onColorChange={(c) => handleAutoCompleteColorSelection(c, "hoverBackground")} /></Col>
				</Row>
				{!isMagento && (
					<>
						<Row className="mb-5 align-items-start">
							<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Additional Search Button Background</h4></label></Col>
							<Col xs={12} md={6}><ColorPickerField key="acAdditionalSearchButtonBackground" initialColor={acDesign.additionalSearchButtonBackground || "#333333"} onColorChange={(c) => handleAutoCompleteColorSelection(c, "additionalSearchButtonBackground")} /></Col>
						</Row>
						<Row className="mb-5 align-items-start">
							<Col xs={12} md={4}><label style={{ marginTop: "10px" }}><h4>Additional Search Button Text</h4></label></Col>
							<Col xs={12} md={6}><ColorPickerField key="acAdditionalSearchButtonText" initialColor={acDesign.additionalSearchButtonText || "#FFFFFF"} onColorChange={(c) => handleAutoCompleteColorSelection(c, "additionalSearchButtonText")} /></Col>
						</Row>
					</>
				)}
				<hr className="my-4" />
				<div className="d-flex justify-content-end">
					<Button disabled={autoCompleteDesignSaveLoader} className="btn-primary cta autoWidth" onClick={(e) => { e.preventDefault(); saveAutoCompleteDesign(); }}>
						{autoCompleteDesignSaveLoader ? <><Spinner size="sm" /> {"Saving..."}</> : "Save"}
					</Button>
				</div>
			</div>
		);
	};

	const supportedPlatforms = [PLATFORMS.MAGENTO, PLATFORMS.SHOPIFY, PLATFORMS.BIGCOMMERCE, PLATFORMS.WOOCOMMERCE, PLATFORMS.DRUPAL];
	if (websiteLoader || !websiteData?.[currentWebsiteId]) return <DetailSkeleton />;
	if (!platform || !supportedPlatforms.includes(platform)) {
		return (
			<div className="designOuterContainer">
				<div className="designInnerContainer">
					<p className="text-muted">Design is not available for this platform.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="designOuterContainer">
			<div className="designInnerContainer">
				{designLoader ? (
					<DetailSkeleton />
				) : (
					<>
						<Row>
							<Col xs={12}>
								<h3 className="mb-3">Design</h3>
								<div style={{ marginBottom: "20px" }}>
									<h5 className="mb-2">Colors</h5>
									<p>You can change colors for some elements of a widget's template. Colors depends on the chosen theme.</p>
								</div>
							</Col>
						</Row>
						{designData ? renderColorsComponent() : <DetailSkeleton />}
						<hr className="my-4" />
						<Row>
							<Col xs={12}>
								<h3>Custom CSS</h3>
								<div style={{ marginBottom: "20px", border: "1px solid #ddd", borderRadius: "5px", overflow: "hidden" }}>
									<JavaScriptCodeEditor key="autoCompleteCustomCss" value={autoCompleteCustomCss} onChange={(value) => setAutoCompleteCustomCss(value)} />
								</div>
							</Col>
						</Row>
						<hr className="my-4" />
						<div className="d-flex justify-content-end">
							<Button disabled={autoCompleteCssSaveLoader} className="btn-primary cta autoWidth" onClick={(e) => { e.preventDefault(); saveAutoCompleteCss(); }}>
								{autoCompleteCssSaveLoader ? <><Spinner size="sm" /> {"Saving..."}</> : "Save"}
							</Button>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default AutoComplete;
